import React, { useEffect, useRef, useState } from "react";
import { drawLine } from "components/two-d-canvas-v1/helpers";
import {
  ballColorsForCircle,
  colorsArr,
  jointsToExclude,
  linesToConnect,
} from "./constants";
import {
  createColoredCircle,
  getScaledX,
  getScaledY,
  getWidthForVideoCanvas,
} from "./helpers";
import styles from "./styles.module.scss";

export const TwodD = ({
  allData,
  currentFrame,
  positions,
  ishandTracerActive,
  isTwoDActive,
  movement_type,
  tracerColor,
  strokeWidth,
}) => {
  const twodAllData = allData?.pose["2d"] || {};
  const ballData = allData?.ball?.["2d"] || {};
  const canvasRef = useRef(null);
  const twodRef = useRef(null);
  const firstPositionKey = movement_type === "batter" ? "p1" : "set";
  const lastPositionKey = movement_type === "batter" ? "p6" : "finish";

  const { height, width } = allData?.configuration?.capturing || {};
  const { clientWidth: canvasWidth, clientHeight: canvasHeight } =
    canvasRef?.current || {};
  const handedness =
    allData?.configuration?.movement?.user_handedness === "leftHanded"
      ? "hand_left"
      : "hand_right";

  const widthForVideoCanvas = getWidthForVideoCanvas({
    containerHeight: twodRef?.current?.clientHeight,
    width,
    height,
  });

  function createCriticalCircle({ positionFrame, context }) {
    if (!positionFrame) return;
    const element = twodAllData[positionFrame][handedness];
    const argumentsForCircle = {
      canvasHeight,
      canvasWidth,
      context,
      element,
      height,
      width,
    };

    colorsArr.forEach((item) =>
      createColoredCircle({
        ...argumentsForCircle,
        color: item.color,
        lineWidth: item.lineWidth,
        filled: item?.filled || undefined,
      }),
    );
  }

  function drawTwodStickFigure() {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (
      currentFrame >= (positions?.[firstPositionKey] || 0) &&
      currentFrame <=
        (positions?.[lastPositionKey] || Object.keys(twodAllData).length - 1)
    ) {
      if (twodAllData && currentFrame <= Object.keys(twodAllData).length) {
        if (ishandTracerActive) {
          // Hand Tracer drawing
          context.strokeStyle = tracerColor;
          context.lineWidth = strokeWidth;
          context.beginPath();

          for (
            let i = positions?.[firstPositionKey] || 0;
            i < currentFrame;
            i++
          ) {
            const point = twodAllData[i][handedness];
            const xCoordinate = point[0];
            const yCoordinate = point[1];

            const scaledX = getScaledX({ canvasWidth, width, xCoordinate });
            const scaledY = getScaledY({ canvasHeight, height, yCoordinate });
            if (i === 0) {
              // Move to the first point
              context.moveTo(scaledX, scaledY);
            } else {
              // Draw a line to the current point
              context.lineTo(scaledX, scaledY);
            }
          }
          context.stroke();

          // Draw 5 circles for 5 critical events with specific colors
          for (const event in positions) {
            if (
              positions.hasOwnProperty(event) &&
              currentFrame > positions[event] - 1
            ) {
              createCriticalCircle({
                positionFrame: positions[event],
                context,
              });
            }
          }
        }

        // 2D pose Drawing
        const pointsArrOnFrameIndex = twodAllData[currentFrame];
        if (pointsArrOnFrameIndex !== undefined && isTwoDActive) {
          context.strokeStyle = "red";
          context.lineWidth = 2;
          context.stroke();

          for (const key in pointsArrOnFrameIndex) {
            if (Object.hasOwnProperty.call(pointsArrOnFrameIndex, key)) {
              const element = pointsArrOnFrameIndex[key];
              const xCoordinate = element[0];
              const yCoordinate = element[1];
              if (
                xCoordinate !== null &&
                yCoordinate !== null &&
                !jointsToExclude.includes(key)
              ) {
                context.beginPath(); // Start a new path for each circle
                context.arc(
                  getScaledX({ canvasWidth, width, xCoordinate }),
                  getScaledY({ canvasHeight, height, yCoordinate }),
                  3,
                  0,
                  2 * Math.PI,
                );
                context.fillStyle = "red";
                context.fill();
                context.closePath(); // Close the path
              }
            }
          }

          context.strokeStyle = "black";

          linesToConnect.forEach((line) => {
            const [key1, key2] = line;
            const firstIndexOfCoordinate = pointsArrOnFrameIndex[key1];
            const secondIndexOfCoordinate = pointsArrOnFrameIndex[key2];

            if (
              firstIndexOfCoordinate &&
              secondIndexOfCoordinate &&
              firstIndexOfCoordinate[0] !== null &&
              firstIndexOfCoordinate[1] !== null &&
              secondIndexOfCoordinate[0] !== null &&
              secondIndexOfCoordinate[1] !== null
            ) {
              const startingX = getScaledX({
                canvasWidth,
                width,
                xCoordinate: firstIndexOfCoordinate[0],
              });
              const startingY = getScaledY({
                canvasHeight,
                height,
                yCoordinate: firstIndexOfCoordinate[1],
              });
              const endingX = getScaledX({
                canvasWidth,
                width,
                xCoordinate: secondIndexOfCoordinate[0],
              });
              const endingY = getScaledY({
                canvasHeight,
                height,
                yCoordinate: secondIndexOfCoordinate[1],
              });
              drawLine(context, startingX, startingY, endingX, endingY);
            }
          });
        }
      }

      if (
        ballData &&
        currentFrame <= Object.keys(ballData).length &&
        ishandTracerActive
      ) {
        // Ball Tracer
        const ballStartingPositionKey =
          movement_type === "batter" ? "impact" : "release";
        for (
          let i = positions?.[ballStartingPositionKey];
          i < currentFrame;
          i++
        ) {
          const element = ballData[i];
          if (element) {
            const argumentsForBallCircle = {
              canvasHeight,
              canvasWidth,
              context,
              element,
              height,
              width,
            };

            ballColorsForCircle.forEach((item) =>
              createColoredCircle({
                ...argumentsForBallCircle,
                color: item.color,
                lineWidth: item.lineWidth,
                filled: item?.filled || undefined,
              }),
            );
          }
        }
      }
    }
  }

  useEffect(() => {
    if (canvasRef.current && twodRef.current) {
      const canvas = canvasRef.current;
      const container = twodRef.current;
      canvas.width = getWidthForVideoCanvas({
        containerHeight: container.clientHeight,
        width,
        height,
      });
      canvas.height = container.clientHeight;
    }
  }, [width, height]);

  useEffect(() => {
    if (twodAllData && ballData) {
      drawTwodStickFigure();
    }
  }, [twodAllData, ballData, currentFrame, ishandTracerActive, isTwoDActive]);

  useEffect(() => {
    setTimeout(() => {
      drawTwodStickFigure();
    }, 0);
  }, []);

  return (
    <div className={styles.twodContainer} ref={twodRef}>
      <canvas
        ref={canvasRef}
        height={twodRef?.current?.clientHeight}
        width={widthForVideoCanvas}
      />
    </div>
  );
};
